import React, { useState } from 'react';
import { AthleteProfile } from '../types';
import { ProRestrictedWrapper } from './common/ProRestrictedWrapper';
import { verifyProAccess } from '../utils/authUtils';
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  CartesianGrid, 
  RadarChart, 
  Radar, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  BarChart, 
  Bar, 
  Legend 
} from 'recharts';
import { 
  Crown, 
  Sparkles, 
  TrendingUp, 
  Award, 
  Zap, 
  Target, 
  Activity, 
  BarChart3, 
  ShieldCheck, 
  ArrowUpRight, 
  Flame 
} from 'lucide-react';

interface AdvancedInsightsProCardProps {
  user: AthleteProfile;
  onOpenProCheckout?: (contextMessage?: string) => void;
}

export const AdvancedInsightsProCard: React.FC<AdvancedInsightsProCardProps> = ({ user, onOpenProCheckout }) => {
  const [insightTab, setInsightTab] = useState<'trends' | 'leaders' | 'comparison' | 'projections'>('leaders');
  const isPro = verifyProAccess(user);

  // Multi-Month Career Historical Trend Data
  const historicalTrendData = [
    { month: 'Mar', scoringAvg: 14.2, efficiency: 18.5, leagueAvg: 12.0, winRate: 55 },
    { month: 'Apr', scoringAvg: 16.8, efficiency: 21.0, leagueAvg: 12.5, winRate: 60 },
    { month: 'May', scoringAvg: 19.5, efficiency: 24.2, leagueAvg: 13.0, winRate: 68 },
    { month: 'Jun', scoringAvg: 22.1, efficiency: 27.8, leagueAvg: 13.2, winRate: 75 },
    { month: 'Jul', scoringAvg: 24.6, efficiency: 30.5, leagueAvg: 13.5, winRate: 82 },
    { month: 'Aug', scoringAvg: 26.8, efficiency: 33.2, leagueAvg: 13.8, winRate: 88 },
  ];

  // Career Cumulative Points Overlay Data (Athlete vs Top 5 League Leaders)
  const careerPointsGrowthData = [
    { game: 'Game 1', athletePTS: 18, leader1: 24, leader2: 22, leader3: 20, leader4: 19, leader5: 17 },
    { game: 'Game 2', athletePTS: 42, leader1: 52, leader2: 48, leader3: 45, leader4: 41, leader5: 38 },
    { game: 'Game 3', athletePTS: 68, leader1: 81, leader2: 74, leader3: 71, leader4: 65, leader5: 60 },
    { game: 'Game 4', athletePTS: 98, leader1: 112, leader2: 104, leader3: 99, leader4: 92, leader5: 84 },
    { game: 'Game 5', athletePTS: 126, leader1: 145, leader2: 132, leader3: 128, leader4: 118, leader5: 110 },
    { game: 'Game 6', athletePTS: 158, leader1: 178, leader2: 162, leader3: 155, leader4: 145, leader5: 136 },
    { game: 'Game 7', athletePTS: 192, leader1: 210, leader2: 195, leader3: 182, leader4: 172, leader5: 162 },
    { game: 'Game 8', athletePTS: 228, leader1: 244, leader2: 228, leader3: 212, leader4: 200, leader5: 188 },
    { game: 'Game 9', athletePTS: 265, leader1: 278, leader2: 260, leader3: 242, leader4: 228, leader5: 214 },
    { game: 'Game 10', athletePTS: 304, leader1: 312, leader2: 290, leader3: 272, leader4: 258, leader5: 240 },
  ];

  const topLeagueLeadersList = [
    { rank: '#1', name: 'Marcus Vance', points: 312, ppg: 31.2, color: '#38bdf8' },
    { rank: 'YOU (#2)', name: user?.name || 'Athlete', points: 304, ppg: 30.4, color: '#a3e635', isUser: true },
    { rank: '#3', name: 'Elena Rostova', points: 290, ppg: 29.0, color: '#c084fc' },
    { rank: '#4', name: 'Deon Carter', points: 272, ppg: 27.2, color: '#f43f5e' },
    { rank: '#5', name: 'Maya Lin', points: 258, ppg: 25.8, color: '#2dd4bf' },
    { rank: '#6', name: 'Jaylen Thorne', points: 240, ppg: 24.0, color: '#fb923c' },
  ];

  // Career Attributes Comparison Radar Data (Athlete vs. Top 10% PRO Athletes)
  const radarComparisonData = [
    { subject: 'Scoring Volume', athlete: 92, proTop10: 88, fullMark: 100 },
    { subject: 'Playmaking (AST)', athlete: 85, proTop10: 82, fullMark: 100 },
    { subject: 'Rebounding', athlete: 78, proTop10: 80, fullMark: 100 },
    { subject: 'Clutch Execution', athlete: 96, proTop10: 90, fullMark: 100 },
    { subject: 'Defensive Motor', athlete: 88, proTop10: 85, fullMark: 100 },
    { subject: 'Court Efficiency', athlete: 94, proTop10: 89, fullMark: 100 },
  ];

  // Sport-specific Pro Scouting Metrics Breakdown
  const scoutingProjections = [
    { metric: 'NCAA D1 / Overseas Rating', score: '94.8 / 100', tier: 'Top 2% Globally', status: 'D1 Ready' },
    { metric: 'Clutch Factor Index', score: '96.2 / 100', tier: '99th Percentile', status: 'Elite' },
    { metric: 'Scout Evaluation Index', score: 'A+ Verified', tier: 'FIBA / NCAA Approved', status: 'Verified' },
    { metric: 'Offensive Synergy Score', score: '1.28 PPP', tier: 'Top 5% League Scorer', status: 'Elite' },
  ];

  return (
    <ProRestrictedWrapper
      user={user}
      onOpenProCheckout={onOpenProCheckout}
      featureTitle="Advanced Career Insights & Pro Radar Matrix"
      featureDescription="Unlock 6-month historical trajectory charts, scout attribute comparisons against top 10% PRO athletes, and D1 recruiting projections."
      customContextMessage="This historical performance deep-dive requires a PRO membership to unlock."
    >
      <div id="advanced-insights-pro-card" className="bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-950 rounded-[2.5rem] p-6 md:p-8 border-2 border-amber-400/80 shadow-[0_0_40px_rgba(251,191,36,0.15)] space-y-6 relative overflow-hidden">
        {/* Background Glow Ambient Accents */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-lime-400/10 rounded-full blur-3xl pointer-events-none" />

        {/* Header Row */}
      <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-5">
        <div className="flex items-center space-x-3.5">
          <div className="p-3 bg-gradient-to-tr from-amber-400 via-amber-300 to-yellow-400 text-black rounded-2xl shadow-xl shadow-amber-400/20 ring-2 ring-amber-300/50">
            <Crown className="w-7 h-7 stroke-[2.5]" />
          </div>

          <div>
            <div className="flex items-center space-x-2">
              <span className="px-3 py-0.5 bg-amber-400 text-black font-black italic text-[10px] uppercase font-mono rounded-full tracking-wider shadow">
                PRO SUBSCRIBER EXCLUSIVE 👑
              </span>
              <span className="text-xs text-amber-300 font-mono font-bold flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Live Recharts Analytics Engine
              </span>
            </div>
            <h2 className="text-xl md:text-2xl font-black italic uppercase text-white tracking-tight mt-1 flex items-center gap-2">
              <span>Advanced Career Insights & Comparisons</span>
            </h2>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center space-x-1.5 bg-indigo-950/90 p-1.5 rounded-2xl border border-white/10 flex-wrap">
          <button
            type="button"
            onClick={() => setInsightTab('leaders')}
            className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
              insightTab === 'leaders'
                ? 'bg-amber-400 text-black shadow-lg shadow-amber-400/20'
                : 'text-indigo-300 hover:text-white'
            }`}
          >
            Top 5 Leaders Overlay
          </button>
          <button
            type="button"
            onClick={() => setInsightTab('trends')}
            className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
              insightTab === 'trends'
                ? 'bg-amber-400 text-black shadow-lg shadow-amber-400/20'
                : 'text-indigo-300 hover:text-white'
            }`}
          >
            Historical Trends
          </button>
          <button
            type="button"
            onClick={() => setInsightTab('comparison')}
            className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
              insightTab === 'comparison'
                ? 'bg-amber-400 text-black shadow-lg shadow-amber-400/20'
                : 'text-indigo-300 hover:text-white'
            }`}
          >
            Skill Benchmark
          </button>
          <button
            type="button"
            onClick={() => setInsightTab('projections')}
            className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
              insightTab === 'projections'
                ? 'bg-amber-400 text-black shadow-lg shadow-amber-400/20'
                : 'text-indigo-300 hover:text-white'
            }`}
          >
            Scout Projections
          </button>
        </div>
      </div>

      {/* TAB 0: TOP 5 LEAGUE LEADERS COMPARATIVE OVERLAY */}
      {insightTab === 'leaders' && (
        <div className="space-y-5 animate-fadeIn">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs font-mono">
            <div className="flex items-center space-x-2 text-amber-300 font-bold uppercase">
              <TrendingUp className="w-4 h-4 text-lime-400" />
              <span>Career Points Growth: You vs. Top 5 League Leaders</span>
            </div>
            <span className="text-[11px] text-lime-400 font-bold bg-lime-400/10 px-2.5 py-0.5 rounded-full border border-lime-400/30">
              Only -8 PTS Behind #1 Marcus Vance (312 PTS)
            </span>
          </div>

          {/* Recharts Comparative Chart Overlay */}
          <div className="w-full h-80 bg-indigo-950/70 p-3.5 rounded-2xl border border-white/10 shadow-2xl">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={careerPointsGrowthData} margin={{ top: 10, right: 30, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff15" vertical={false} />
                <XAxis dataKey="game" stroke="#a5b4fc" fontSize={11} tickLine={false} />
                <YAxis stroke="#a5b4fc" fontSize={11} tickLine={false} axisLine={false} unit=" PTS" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#090d16', borderColor: '#a3e635', borderRadius: '1rem', color: '#fff' }}
                  formatter={(val: any, name: any) => [`${val} PTS`, name]}
                />
                <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                
                {/* Bold Athlete Line */}
                <Line
                  type="monotone"
                  dataKey="athletePTS"
                  name={`YOU (${user?.name || 'Athlete'})`}
                  stroke="#a3e635"
                  strokeWidth={4}
                  dot={{ r: 6, fill: '#a3e635', strokeWidth: 2, stroke: '#000' }}
                  activeDot={{ r: 8, fill: '#a3e635' }}
                />

                {/* Top 5 League Leaders Lines */}
                <Line type="monotone" dataKey="leader1" name="#1 Marcus Vance (312 PTS)" stroke="#38bdf8" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                <Line type="monotone" dataKey="leader2" name="#2 Elena Rostova (290 PTS)" stroke="#c084fc" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                <Line type="monotone" dataKey="leader3" name="#3 Deon Carter (272 PTS)" stroke="#f43f5e" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                <Line type="monotone" dataKey="leader4" name="#4 Maya Lin (258 PTS)" stroke="#2dd4bf" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                <Line type="monotone" dataKey="leader5" name="#5 Jaylen Thorne (240 PTS)" stroke="#fb923c" strokeWidth={2} strokeDasharray="4 4" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Leaders Breakdown Cards Table */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {topLeagueLeadersList.map((leader, idx) => (
              <div
                key={idx}
                className={`p-3.5 rounded-2xl border transition-all duration-300 space-y-1 relative overflow-hidden ${
                  leader.isUser
                    ? 'bg-lime-400 text-black border-lime-400 ring-2 ring-lime-400/50 shadow-lg shadow-lime-400/20'
                    : 'bg-indigo-950/80 text-white border-white/10 hover:border-amber-400/40'
                }`}
              >
                <div className="flex items-center justify-between text-[10px] font-mono font-black uppercase">
                  <span>{leader.rank}</span>
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: leader.color }} />
                </div>

                <div className={`font-black text-xs truncate ${leader.isUser ? 'text-black' : 'text-white'}`}>
                  {leader.name}
                </div>

                <div className={`text-lg font-black italic ${leader.isUser ? 'text-black' : 'text-amber-300'}`}>
                  {leader.points} PTS
                </div>

                <div className={`text-[10px] font-mono font-bold ${leader.isUser ? 'text-black/80' : 'text-indigo-200/70'}`}>
                  {leader.ppg} PPG Avg
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 1: HISTORICAL TRENDS (LINE & AREA CHART) */}
      {insightTab === 'trends' && (
        <div className="space-y-4 animate-fadeIn">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs font-mono text-indigo-200">
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-4 h-4 text-lime-400" />
              <span className="font-bold text-white uppercase">6-Month Career Progression Trajectory</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="flex items-center space-x-1.5">
                <span className="w-3 h-3 rounded-full bg-amber-400 inline-block" />
                <span className="text-white">Your PTS Avg</span>
              </span>
              <span className="flex items-center space-x-1.5">
                <span className="w-3 h-3 rounded-full bg-lime-400 inline-block" />
                <span className="text-white">Efficiency Index</span>
              </span>
              <span className="flex items-center space-x-1.5">
                <span className="w-3 h-3 rounded-full bg-slate-500 inline-block" />
                <span className="text-indigo-300">League Avg</span>
              </span>
            </div>
          </div>

          <div className="w-full h-64 bg-indigo-950/60 p-3 rounded-2xl border border-white/10">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={historicalTrendData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff15" vertical={false} />
                <XAxis dataKey="month" stroke="#a5b4fc" fontSize={11} tickLine={false} />
                <YAxis stroke="#a5b4fc" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#090d16', borderColor: '#fbbf24', borderRadius: '1rem', color: '#fff' }}
                />
                <Line type="monotone" dataKey="scoringAvg" stroke="#fbbf24" strokeWidth={3} dot={{ r: 5, fill: '#fbbf24' }} />
                <Line type="monotone" dataKey="efficiency" stroke="#a3e635" strokeWidth={3} dot={{ r: 5, fill: '#a3e635' }} />
                <Line type="monotone" dataKey="leagueAvg" stroke="#64748b" strokeWidth={2} strokeDasharray="4 4" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3.5 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-1">
              <span className="text-[10px] font-black uppercase text-indigo-300">Scoring Rate Growth</span>
              <div className="text-xl font-black italic text-lime-400 flex items-center gap-1">
                <ArrowUpRight className="w-5 h-5 stroke-[3]" />
                <span>+88.7% since Mar</span>
              </div>
              <p className="text-[11px] text-indigo-200/70">Averaging 26.8 PPG over recent 10 league appearances.</p>
            </div>

            <div className="p-3.5 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-1">
              <span className="text-[10px] font-black uppercase text-indigo-300">Win Rate Trajectory</span>
              <div className="text-xl font-black italic text-amber-300 flex items-center gap-1">
                <Flame className="w-5 h-5 fill-amber-400" />
                <span>88% Win Rate</span>
              </div>
              <p className="text-[11px] text-indigo-200/70">Won 14 out of last 16 pickup and tournament games.</p>
            </div>

            <div className="p-3.5 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-1">
              <span className="text-[10px] font-black uppercase text-indigo-300">PER Efficiency Rating</span>
              <div className="text-xl font-black italic text-cyan-300 flex items-center gap-1">
                <Zap className="w-5 h-5 fill-cyan-400" />
                <span>33.2 PER</span>
              </div>
              <p className="text-[11px] text-indigo-200/70">Top 1.5% overall player efficiency across all regions.</p>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: LEAGUE BENCHMARK COMPARISON (RADAR & BAR CHART) */}
      {insightTab === 'comparison' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center animate-fadeIn">
          {/* Radar Chart */}
          <div className="space-y-2">
            <div className="text-xs font-mono font-bold text-amber-300 uppercase flex items-center gap-1.5">
              <Award className="w-4 h-4 text-amber-400" />
              <span>Skill Matrix: Athlete vs. Top 10% PRO Benchmark</span>
            </div>
            <div className="w-full h-64 bg-indigo-950/60 p-2 rounded-2xl border border-white/10 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="75%" data={radarComparisonData}>
                  <PolarGrid stroke="#ffffff20" />
                  <PolarAngleAxis dataKey="subject" stroke="#a5b4fc" fontSize={10} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="#ffffff20" />
                  <Radar name="Your Career" dataKey="athlete" stroke="#fbbf24" fill="#fbbf24" fillOpacity={0.5} />
                  <Radar name="Top 10% PRO Avg" dataKey="proTop10" stroke="#a3e635" fill="#a3e635" fillOpacity={0.3} />
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Key Insights List */}
          <div className="space-y-3">
            <div className="p-4 bg-indigo-950/80 rounded-2xl border border-amber-400/40 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-extrabold text-white flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-amber-400" />
                  Clutch Execution Rating
                </span>
                <span className="text-xs font-black font-mono text-lime-400">96 / 100</span>
              </div>
              <p className="text-xs text-indigo-200/80">
                You outperform 96% of league athletes in 4th-quarter score differentials and game-winning shot conversions.
              </p>
            </div>

            <div className="p-4 bg-indigo-950/80 rounded-2xl border border-amber-400/40 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-extrabold text-white flex items-center gap-1.5">
                  <Target className="w-4 h-4 text-amber-400" />
                  Offensive Volume Efficiency
                </span>
                <span className="text-xs font-black font-mono text-lime-400">92 / 100</span>
              </div>
              <p className="text-xs text-indigo-200/80">
                Scoring output matches Division 1 college guard benchmarks, with 54% true shooting percentage.
              </p>
            </div>

            <div className="p-4 bg-indigo-950/80 rounded-2xl border border-amber-400/40 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-extrabold text-white flex items-center gap-1.5">
                  <Zap className="w-4 h-4 text-amber-400" />
                  Court Motor & Defensive Impact
                </span>
                <span className="text-xs font-black font-mono text-cyan-300">88 / 100</span>
              </div>
              <p className="text-xs text-indigo-200/80">
                High steal-to-turnover ratio (2.4x) confirms defensive discipline and fast-break initiation.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: SCOUT PROJECTIONS */}
      {insightTab === 'projections' && (
        <div className="space-y-4 animate-fadeIn">
          <div className="text-xs font-mono font-bold text-amber-300 uppercase flex items-center gap-1.5">
            <Crown className="w-4 h-4 text-amber-400" />
            <span>AI & Verified Scout Recruiting Evaluation Index</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {scoutingProjections.map((proj, idx) => (
              <div key={idx} className="p-4 bg-indigo-950/90 rounded-2xl border border-amber-400/30 space-y-2">
                <span className="text-[10px] font-black uppercase text-indigo-300 font-mono block">
                  {proj.metric}
                </span>
                <div className="text-xl font-black italic text-amber-300">
                  {proj.score}
                </div>
                <div className="flex items-center justify-between text-[10px] font-mono pt-1 border-t border-white/10">
                  <span className="text-indigo-200">{proj.tier}</span>
                  <span className="text-lime-400 font-bold uppercase">{proj.status}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 bg-indigo-900/60 rounded-2xl border border-white/10 flex items-center justify-between text-xs font-mono text-indigo-200">
            <span>Official Scout Reel Export Available in Scouts Portal.</span>
            <span className="text-amber-300 font-bold">PRO Member Verified ID: {user.paymentReceiptId || 'VERIFIED-PRO-2026'}</span>
          </div>
        </div>
      )}
    </div>
    </ProRestrictedWrapper>
  );
};
